import React from 'react';
import { Map, List, MapPin, Menu, X, Filter, Calendar } from 'lucide-react';

interface NavigationProps {
  activeView: 'map' | 'activities';
  onViewChange: (view: 'map' | 'activities') => void;
  selectedYear: string;
  onYearChange: (year: string) => void;
  availableYears: string[];
  totalActivities: number;
  filteredActivities: number;
}

export const Navigation: React.FC<NavigationProps> = ({ 
  activeView, 
  onViewChange, 
  selectedYear, 
  onYearChange, 
  availableYears, 
  totalActivities, 
  filteredActivities 
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);

  return (
    <nav className="bg-white shadow-lg border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo y título */}
          <div className="flex items-center space-x-3">
            <img 
              src="https://www.igcsl.org/igc.png" 
              alt="Logo" 
              className="h-8 w-8 object-contain flex-shrink-0"
            />
            <span className="text-lg sm:text-xl font-bold text-gray-900 truncate">MapTracker</span>
          </div>
          
          {/* Navegación y filtros desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Filtros desktop */}
            <div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg">
              <Filter className="h-4 w-4 text-gray-600" />
              <select
                value={selectedYear}
                onChange={(e) => onYearChange(e.target.value)}
                className="bg-transparent border-none text-sm focus:ring-0 focus:outline-none text-gray-700"
              >
                <option value="">Todas las fechas</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                {filteredActivities}/{totalActivities}
              </span>
            </div>

            {/* Navegación desktop */}
            <button
              onClick={() => onViewChange('map')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                activeView === 'map'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Map className="h-4 w-4" />
              <span>Ver Mapa</span>
            </button>
            
            <button
              onClick={() => onViewChange('activities')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                activeView === 'activities'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <List className="h-4 w-4" />
              <span>Ver Actividades</span>
            </button>
          </div>

          {/* Navegación tablet */}
          <div className="hidden md:flex lg:hidden items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                {selectedYear || 'Todas'}
              </span>
            </button>
            
            <button
              onClick={() => onViewChange('map')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'map'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Map className="h-4 w-4" />
            </button>
            
            <button
              onClick={() => onViewChange('activities')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'activities'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Botón menú móvil */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 hover:text-blue-600 p-2 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Filtros tablet desplegables */}
        {showFilters && (
          <div className="hidden md:block lg:hidden border-t border-gray-200 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-medium text-gray-700">Filtrar por año:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => onYearChange(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Todas las fechas</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-600">
                Mostrando: <span className="font-medium">{filteredActivities}</span> de <span className="font-medium">{totalActivities}</span>
              </div>
            </div>
          </div>
        )}

        {/* Menú móvil */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-3">
            {/* Filtros móvil */}
            <div className="px-4 py-3 bg-gray-50 rounded-lg mb-3">
              <div className="flex items-center space-x-2 mb-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Filtros</span>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Filtrar por año</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => onYearChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">Todas las fechas</option>
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-xs text-gray-600">
                  Mostrando: <span className="font-medium">{filteredActivities}</span> de <span className="font-medium">{totalActivities}</span>
                </div>
              </div>
            </div>

            {/* Navegación móvil */}
            <div className="flex flex-col space-y-1">
              <button
                onClick={() => {
                  onViewChange('map');
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeView === 'map'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Map className="h-5 w-5" />
                <span>Ver Mapa</span>
              </button>
              
              <button
                onClick={() => {
                  onViewChange('activities');
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeView === 'activities'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <List className="h-5 w-5" />
                <span>Ver Actividades</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};